import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Form, notification, Button, Input, Card, Spin, Tooltip, Typography } from 'antd';
import {
	Mic,
	PhoneIncoming,
	X,
	Phone,
	Settings,
	Rocket,
	CheckCircle,
	AlertCircle,
	LockIcon,
	ArrowLeft,
} from 'lucide-react';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput';
import { useMutation, useQuery } from '@tanstack/react-query';
import { makeTestCall, updateTestingNumber, getProvisionedNumber } from '@/api/reservedNumbers';
import { sendOTP, verifyOTP } from '@/api/otp';
import { handlePrefixPlusOnPhone } from '@/utils/helper';
// import AudioCall from './AudioCall';
import './TestReceptionist.scss';
import { setCurrentPage, setTestingNumber } from '@/store/account/slice';
import { useDispatch } from 'react-redux';
import PlansModal from '@/components/PlansModal/PlansModal';
import { PhoneNumberSettings } from '@/pages/ReceptionistDashboard/TestItOut/v2/PhoneNumberSettings';
import ForwardingSetupInstructions from '@/pages/GettingStarted/ForwardingSetupInstructions';
import ReCaptchaVerification from '@/components/ReCaptchaVerification';
import { getUser, updateUser, checkVoiceCredits } from '@/api/user';
import { User } from '@/types/TAccounts';
import SourceSelectorModal from './SourceSelectorModal';
import posthog from 'posthog-js';
import CopyableText from '@/components/LabelWithdescription/CopyableText';
import AICustomizationStep from './AICustomizationStep/AICustomizationStep';
import InsufficientCreditsModal from '@/components/InsufficientCreditsModal/InsufficientCreditsModal';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { getResellerLogo } from '@/api/unbrandedReceptionist';
import { getLoggedInUser, getSelectedAgentId } from '@/store/account/selector';
import CsmCard from '@/pages/ReceptionistDashboard/Dashboard/CsmCard';
import { CSM_MEMBERS } from 'src/constants';
import CallComponent from '../Playground/MakeCall';
import { USER_TYPES } from 'src/enums/EUser';
import { setPhoneNumber } from '@/store/phoneNumber/slice';
import { useClarity } from 'src/Hooks/useClarity';

const { Title } = Typography;

type TestingOption =
	| 'test-ai'
	| 'join-audio'
	| 'receive-call'
	| 'tweak-ai'
	| 'start-trial'
	| 'get-number'
	| 'forward-calls'
	| null;

const TestReceptionist: React.FC = () => {
	const clarity = useClarity();
	const [selectedOption, setSelectedOption] = useState<TestingOption>(null);
	const [isHovered, setIsHovered] = useState<TestingOption | string>(null);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const loggedInUser = useAppSelector(getLoggedInUser);
	// const selectedAgent = useAppSelector(getSelectedAgent);
	const {
		data: userData,
		refetch,
		isLoading,
	} = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId as string),
		enabled: !!selectedAgentId,
	});
	const selectedAgent = userData || ({} as User);

	const [testCompleted, setTestCompleted] = useState(false);
	const [form] = Form.useForm();
	const [sendError, setSendError] = useState<string | null>(null);
	const [otpSent, setOtpSent] = useState(false);
	const [otp, setOtp] = useState('');
	const [otpError, setOtpError] = useState<string | null>(null);
	const [verified, setVerified] = useState(false);
	const [sending, setSending] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [currentPhoneNumber, setCurrentPhoneNumber] = useState<string>('');
	const [showChangeNumber, setShowChangeNumber] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [provisionedNumber, setProvisionedNumber] = useState<string>('');
	const [triggerVerification, setTriggerVerification] = useState(false);
	const [showVerification, setShowVerification] = useState(false);
	const [pendingOption, setPendingOption] = useState<TestingOption>(null);
	const [isSourceSelectorModalOpen, setIsSourceSelectorModalOpen] = useState(false);
	const [localTestingNumber, setLocalTestingNumber] = useState<string | null>(null);
	const [insufficientCreditsModalVisible, setInsufficientCreditsModalVisible] = useState(false);
	const [isLoadingCredits, setIsLoadingCredits] = useState(false);
	const [selectedOptionLoading, setSelectedOptionLoading] = useState<TestingOption | null>(null);

	const currentReseller = useAppSelector(getResellerData);

	const isHireReceptionistPage = useAppSelector(
		(state) => state.account.currentPage === ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST,
	);

	const csmMember =
		CSM_MEMBERS.find((member) => member.email === loggedInUser?.csm) || CSM_MEMBERS[0];

	const callLogsLength = userData?.totalCalls || 0;

	const checkCreditsMutation = useMutation({
		mutationKey: ['checkVoiceCredits'],
		mutationFn: checkVoiceCredits,
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'Failed to check credits. Please try again.',
			});
		},
	});
	const handleMarkAsComplete = () => {
		try {
			console.log('handleMarkAsComplete called');
			posthog.capture('onb_v3_test_completed');
			clarity('event', 'onb_v3_test_completed');
			setTestCompleted(true);
			setSelectedOption(null);
		} catch (error) {
			console.error('Error in handleMarkAsComplete:', error);
			// Fallback navigation if the normal flow fails
			try {
				setSelectedOption(null);
			} catch (fallbackError) {
				console.error('Fallback navigation failed:', fallbackError);
			}
		}
	};

	useEffect(() => {
		if (currentPhoneNumber) {
			setOtpSent(false);
			setVerified(false);
			setOtp('');
			setSendError('');
			setOtpError('');
		}
		if (!currentPhoneNumber && loggedInUser?.userType === USER_TYPES.SUB_ACCOUNT) {
			handleMarkAsComplete();
		}
	}, [currentPhoneNumber]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setTriggerVerification(true);
		}, 500);

		return () => clearTimeout(timer);
	}, []);

	const RECAPTCHA_SITE_KEY = import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string;

	useEffect(() => {
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
		script.async = true;
		document.head.appendChild(script);

		return () => {
			document.head.removeChild(script);
		};
	}, []);

	const makeVoidTestCall = useMutation({
		mutationKey: ['testCall'],
		mutationFn: makeTestCall,
		onSuccess() {
			notification.success({
				message: 'Call initiated',
				description: 'You will receive a call shortly',
			});
		},
		onError() {
			notification.error({
				message: 'Call failed',
				description: 'Failed to initiate call. Please try again.',
			});
		},
	});

	const dispatch = useDispatch();

	const { mutate: changeTestingNumberMutation } = useMutation({
		mutationKey: ['changeTestingNumber'],
		mutationFn: updateTestingNumber,
		onSuccess(_, variables) {
			dispatch(setTestingNumber({ testing_number: variables.testing_number }));
			notification.success({ message: 'Testing number changed successfully' });
		},
		onError(error) {
			notification.error({ message: 'Error', description: error.message });
		},
	});

	const getProvisionNumberMutation = useMutation({
		mutationKey: ['provisionedNumberApi'],
		mutationFn: getProvisionedNumber,
		onSuccess(data) {
			setProvisionedNumber(data.provisioned_number || '');
			dispatch(setPhoneNumber({ phoneNumber: data.provisioned_number || '' }));
		},
	});

	useEffect(() => {
		getProvisionNumberMutation.mutate(selectedAgentId as string);
	}, [selectedAgent?.number]);

	useEffect(() => {
		if (selectedAgent?.testing_number) {
			setLocalTestingNumber(selectedAgent?.testing_number);
		}
	}, [selectedAgent?.testing_number]);

	const { data } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: currentReseller.logo as string }),
		enabled: !!(loggedInUser?.resellerClient && currentReseller?.logo),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const resellerLogoUrl = useMemo(() => {
		if (!data) {
			return;
		}
		return URL.createObjectURL(data);
	}, [data]);

	const checkCreditsAndProceed = async (option: TestingOption) => {
		if (option !== 'join-audio' && option !== 'receive-call') {
			if (option === 'test-ai') {
				const totalCalls = userData?.totalCalls || 0;
				if (totalCalls === 2 && !loggedInUser?.whereFoundUs) {
					setIsSourceSelectorModalOpen(true);
				} else {
					setSelectedOption(option);
				}
			} else {
				setSelectedOption(option);
			}
			return;
		}

		setSelectedOptionLoading(option);
		setIsLoadingCredits(true);
		try {
			const creditCheckResult = await checkCreditsMutation.mutateAsync({
				agentType: selectedAgent?.type as 'Inbound' | 'Outbound',
			});
			if (!creditCheckResult?.hasCredits) {
				setInsufficientCreditsModalVisible(true);
				return;
			}
			setShowVerification(true);
			setPendingOption(option);
		} finally {
			setIsLoadingCredits(false);
			setSelectedOptionLoading(null);
		}
	};

	const handleOptionSelect = (option: TestingOption, firstEvent: boolean = false) => {
		posthog.capture(firstEvent ? 'onb_v3_first_time_option_selected' : 'onb_v3_option_selected', {
			option,
		});
		clarity('event', firstEvent ? 'onb_v3_first_time_option_selected' : 'onb_v3_option_selected');
		checkCreditsAndProceed(option);
	};

	const handleStopAudio = () => {
		try {
			console.log('handleStopAudio called');
			posthog.capture('onb_v3_audio_stopped');
			clarity('event', 'onb_v3_audio_stopped');

			// Use setTimeout to ensure this runs after any potential errors in the event listener
			setTimeout(() => {
				console.log('FINAL NAVIGATION HAPPENING');
				handleMarkAsComplete();
			}, 500);
		} catch (error) {
			console.error('Error in handleStopAudio:', error);
			// Attempt direct navigation as fallback
			try {
				setSelectedOption(null);
			} catch (fallbackError) {
				console.error('Fallback navigation failed:', fallbackError);
			}
		}
	};

	const handleSendOTP = async () => {
		const phoneNumber = handlePrefixPlusOnPhone(form.getFieldValue('testing_number'));
		if (!phoneNumber) {
			return;
		}

		posthog.capture('otp_requested');
		clarity('event', 'otp_requested');

		try {
			setSending(true);
			setSendError('');
			setOtpError('');
			const response = await sendOTP(phoneNumber);

			if (response.success) {
				posthog.capture('onb_v3_otp_sent', { success: true });
				clarity('event', 'onb_v3_otp_sent');
				setOtpSent(true);
			} else {
				posthog.capture('onb_v3_otp_sent', { success: false, error: response.message });
				clarity('event', 'onb_v3_otp_sent');
				throw new Error(response.message || 'Failed to send verification code');
			}
		} catch (err: any) {
			setSendError(err.message || 'Failed to send code. Please try again later');
		} finally {
			setSending(false);
		}
	};

	const handleVerifyOTP = async () => {
		if (!otp || otp.length < 4) {
			setOtpError('Please enter a valid verification code');
			return;
		}

		const phoneNumber = handlePrefixPlusOnPhone(form.getFieldValue('testing_number'));
		if (!phoneNumber) {
			return;
		}

		try {
			setVerifying(true);
			setOtpError('');
			const response = await verifyOTP(phoneNumber, otp);

			if (response.success) {
				posthog.capture('onb_v3_otp_verified', { success: true });
				clarity('event', 'onb_v3_otp_verified');

				try {
					setIsSubmitting(true);
					await changeTestingNumberMutation({
						agentId: selectedAgentId as string,
						testing_number: phoneNumber,
					});
					setVerified(true);
					setShowChangeNumber(false);
					setOtpSent(false);
					setOtp('');
					setLocalTestingNumber(phoneNumber);
					await refetch();
					if (pendingOption === 'receive-call' || selectedOption === 'receive-call') {
						await makeVoidTestCall.mutateAsync(selectedAgentId as string);
						notification.success({
							message: 'Security Check Complete',
							description: 'You will receive a call shortly.',
						});
					} else {
						notification.success({
							message: 'Security Check Complete',
							description: 'You can now proceed with your audio call.',
						});
					}
				} catch (error: any) {
					posthog.capture('onb_v3_otp_verification_error', { error: error.message });
					clarity('event', 'onb_v3_otp_verification_error');

					if (error?.response?.status === 409 || error?.message?.includes('already associated')) {
						notification.error({
							message: 'Number Already Associated',
							description:
								'This testing number is already associated with another account. Please use a different number.',
						});
					} else {
						notification.error({
							message: 'Error',
							description: error.message || 'Failed to process your request.',
						});
					}

					setVerified(false);
					setShowChangeNumber(true);
					setLocalTestingNumber(null);
				}
			} else {
				posthog.capture('onb_v3_otp_verified', { success: false, error: response.message });
				clarity('event', 'onb_v3_otp_verified');
				throw new Error(response.message || 'Invalid verification code');
			}
		} catch (err: any) {
			setOtpError(err.message || 'Verification failed. Please try again');
			setVerified(false);
			setShowChangeNumber(true);
			setLocalTestingNumber(null);
		} finally {
			setVerifying(false);
			setIsSubmitting(false);
		}
	};

	const forwardingCompleteMutation = useMutation({
		mutationKey: ['updateForwardingComplete'],
		mutationFn: () => updateUser({ onboardingCompleted: true, agentId: selectedAgentId as string }),
		onSuccess: async () => {
			await refetch().then(() => {
				setSelectedOption(null);
			});
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'Failed to update progress. Please try again.',
			});
		},
	});

	const hasTestingNumber = selectedAgent?.testing_number || localTestingNumber;

	useEffect(() => {
		if (selectedOption === 'receive-call' && selectedAgent?.testing_number) {
			makeVoidTestCall.mutate(selectedAgentId as string);
		}
	}, [selectedOption]);

	// Add automatic navigation for receive-call after 5 seconds
	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;

		if (selectedOption === 'receive-call' && hasTestingNumber && !showChangeNumber) {
			// Set a timer to automatically return to the index page after 5 seconds
			console.log('Starting 5-second automatic navigation timer for receive-call');
			timer = setTimeout(() => {
				console.log('Auto-navigation timer completed, returning to main page');
				handleMarkAsComplete();
			}, 5000);
		}

		// Clean up the timer if component unmounts or selection changes
		return () => {
			if (timer) {
				console.log('Clearing auto-navigation timer');
				clearTimeout(timer);
			}
		};
	}, [selectedOption, hasTestingNumber, showChangeNumber, handleMarkAsComplete]);

	const renderTrialCard = () => {
		if (
			!(
				(loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
				(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)
			)
		) {
			if (!provisionedNumber) {
				return null;
			}

			return (
				<motion.div
					whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.98 }}
					className="option-card relative highlight-card"
					animate={{
						scale: [1, 1.02, 1],
						boxShadow: [
							'0 0 0 0 rgba(99, 102, 241, 0)',
							'0 0 0 15px rgba(99, 102, 241, 0.1)',
							'0 0 0 0 rgba(99, 102, 241, 0)',
						],
					}}
					transition={{
						duration: 2,
						repeat: Infinity,
						ease: 'easeInOut',
					}}
					onHoverStart={() => setIsHovered('forward-calls')}
					onHoverEnd={() => setIsHovered(null)}
					onClick={() => handleOptionSelect('forward-calls')}
				>
					<div className="absolute top-3 right-3 bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded-full">
						Recommended
					</div>
					<div className="option-icon-container bg-blue-100">
						<Rocket className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
					</div>
					<h3>Set Up Call Forwarding</h3>
					<p className="text-gray-600">Connect AI to your business</p>
				</motion.div>
			);
		}

		// Special return for demo users - full row "Start Trial" button
		return (
			<div className="trial-card-container">
				<motion.div
					whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.98 }}
					className="option-card relative highlight-card col-span-3"
					animate={{
						scale: [1, 1.02, 1],
						boxShadow: [
							'0 0 0 0 rgba(99, 102, 241, 0)',
							'0 0 0 15px rgba(99, 102, 241, 0.1)',
							'0 0 0 0 rgba(99, 102, 241, 0)',
						],
					}}
					transition={{
						duration: 2,
						repeat: Infinity,
						ease: 'easeInOut',
					}}
					onHoverStart={() => setIsHovered('start-trial')}
					onHoverEnd={() => setIsHovered(null)}
					onClick={() => {
						loggedInUser?.resellerClient
							? dispatch(setCurrentPage(ReceptionistDashboardSiderItem.RESELLER_PLAN))
							: handleOptionSelect('start-trial');
					}}
				>
					<div className="absolute top-3 right-3 bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded-full">
						Recommended
					</div>
					<div className="option-icon-container bg-blue-100">
						<Rocket className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
					</div>
					<h3>Hire AI Receptionist</h3>
					{!loggedInUser?.resellerClient && <p className="text-gray-600">Start 7-day free trial</p>}
				</motion.div>

				{getProvisionNumberMutation.status === 'success' &&
					((loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
						(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)) && (
						<div className="secondary-options">
							<motion.div
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className="option-card"
								onHoverStart={() => setIsHovered('test-ai')}
								onHoverEnd={() => setIsHovered(null)}
								onClick={() => handleOptionSelect('test-ai')}
							>
								<div className="option-icon-container bg-blue-100">
									<Phone className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
								</div>
								<h3>Test Again</h3>
								<p className="text-gray-600">Try out your AI Receptionist again</p>
							</motion.div>

							<motion.div
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className="option-card"
								onHoverStart={() => setIsHovered('tweak-ai')}
								onHoverEnd={() => setIsHovered(null)}
								onClick={() => handleOptionSelect('tweak-ai')}
							>
								<div className="option-icon-container bg-blue-100">
									<Settings className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
								</div>
								<h3>Tweak My AI</h3>
								<p className="text-gray-600">Fine-tune your AI Receptionist</p>
							</motion.div>
						</div>
					)}
			</div>
		);
	};

	const handleVerificationComplete = (success: boolean) => {
		if (success) {
			setSelectedOption(pendingOption);
			setShowVerification(false);
			setPendingOption(null);
		} else {
			setTriggerVerification(false);
		}
	};

	const renderVerificationScreen = () => (
		<div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 flex justify-center items-center p-4">
			<div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
			<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_800px_at_100%_200px,rgba(110,121,214,0.1),transparent)]" />
			<Card className="w-full max-w-md border-2 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
				<div className="p-8 space-y-6 text-center">
					<h2 className="text-2xl font-bold text-gray-900">Verifying Access</h2>
					<ReCaptchaVerification
						trigger={triggerVerification}
						onVerificationComplete={handleVerificationComplete}
					/>
				</div>
			</Card>
		</div>
	);

	const handleSourceSelectorComplete = () => {
		posthog.capture('onb_v3_source_selector_completed');
		clarity('event', 'onb_v3_source_selector_completed');
		setIsSourceSelectorModalOpen(false);
		refetch();
		setSelectedOption('test-ai');
	};

	const handleStartTrial = () => {
		setInsufficientCreditsModalVisible(false);
		handleOptionSelect('start-trial');
	};

	const handleManageCredits = () => {
		setInsufficientCreditsModalVisible(false);
		dispatch(setCurrentPage(ReceptionistDashboardSiderItem.CREDITS));
	};

	const renderOptionCard = (
		option: TestingOption,
		icon: React.ReactNode,
		title: string,
		description: string,
		isFirstTime: boolean = false,
	) => (
		<motion.div
			whileHover={{ scale: 1.02 }}
			whileTap={{ scale: 0.98 }}
			className="option-card"
			onHoverStart={() => setIsHovered(option)}
			onHoverEnd={() => setIsHovered(null)}
			onClick={() => handleOptionSelect(option, isFirstTime)}
		>
			<div className="option-icon-container blue">
				<motion.div
					className="option-icon"
					animate={
						isHovered === option
							? {
									scale: [1, 1.2, 1],
									rotate: [0, 10, -10, 0],
								}
							: {}
					}
					transition={{ duration: 1.5, repeat: Infinity }}
				>
					{isLoadingCredits && option === selectedOptionLoading ? <Spin size="small" /> : icon}
				</motion.div>
				<motion.div
					className="option-glow"
					animate={
						isHovered === option
							? {
									scale: [1, 1.5, 1],
									opacity: [0.5, 0, 0.5],
								}
							: {}
					}
					transition={{ duration: 2, repeat: Infinity }}
				/>
			</div>
			<h3>{title}</h3>
			<p className="text-gray-700">{description}</p>
		</motion.div>
	);

	const renderFirstTimeOptions = () => (
		<div className="options-grid first-time">
			{renderOptionCard(
				'join-audio',
				<Mic className="h-6 w-6 md:h-8 md:w-8" />,
				'Join by Audio',
				'Test directly in your browser',
				true,
			)}
			{renderOptionCard(
				'receive-call',
				<PhoneIncoming className="h-6 w-6 md:h-8 md:w-8" />,
				'Receive a Call',
				'Get a call from your AI',
				true,
			)}
		</div>
	);

	const content = {
		'test-ai': (
			<div className="step-container">
				<div className="step-content">
					<div className="step-header">
						<X
							onClick={() => setSelectedOption(null)}
							className="hover:cursor-pointer hover:bg-gray-200 rounded-lg"
						/>
					</div>

					<div className="step-body test-ai-step-body">
						<div className="test-options-container">
							<h2 className="text-2xl md:text-3xl font-semibold text-center mb-4 md:mb-8">
								Choose how you'd like to test
							</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full mx-auto max-w-screen-md">
								<motion.div
									className="option-card w-full"
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									onClick={() => handleOptionSelect('join-audio')}
								>
									<div className="option-icon-container blue">
										{isLoadingCredits && selectedOptionLoading === 'join-audio' ? (
											<Spin size="large" />
										) : (
											<Mic className="h-6 w-6 md:h-8 md:w-8 text-white" />
										)}
									</div>
									<h3>Join by Audio</h3>
									<p className="text-gray-700">Test directly in your browser</p>
								</motion.div>

								<motion.div
									className="option-card w-full"
									whileHover={{ scale: 1.02 }}
									whileTap={{ scale: 0.98 }}
									onClick={() => handleOptionSelect('receive-call')}
								>
									<div className="option-icon-container green">
										{isLoadingCredits && selectedOptionLoading === 'receive-call' ? (
											<Spin size="large" />
										) : (
											<PhoneIncoming className="h-6 w-6 md:h-8 md:w-8 text-white" />
										)}
									</div>
									<h3>Receive a Call</h3>
									<p className="text-gray-700">Get a call from your AI</p>
								</motion.div>
							</div>
						</div>
					</div>
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),
		'join-audio': (
			<div className="step-container">
				<div className="step-content">
					<div className="step-header">
						<h1 className="sm:text-3xl text-2xl font-bold mb-0">Join Audio Call</h1>
						<div />
					</div>

					{!(isSubmitting || isLoading) ? (
						<div className="step-body flex flex-col items-center justify-center">
							{hasTestingNumber && !showChangeNumber ? (
								<div translate="no">
									{/* <AudioCall handleStopAudio={handleStopAudio} /> */}
									<CallComponent handleStopAudio={handleStopAudio} />
								</div>
							) : (
								<motion.div
									className="phone-setup-container"
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									transition={{ duration: 0.3 }}
								>
									<motion.div
										className="setup-icon-container"
										animate={{
											scale: [1, 1.1, 1],
										}}
										transition={{
											duration: 2,
											repeat: Infinity,
											ease: 'easeInOut',
										}}
									>
										<Phone className="setup-icon text-[#6366F1]" />
									</motion.div>

									<Title level={3}>
										Please enter your phone number for a brief, one-time security verification
									</Title>

									<Form
										form={form}
										layout="vertical"
										className="phone-form-tr"
										onValuesChange={(_, allValues) => {
											if (allValues.testing_number) {
												setCurrentPhoneNumber(allValues.testing_number);
											}
										}}
									>
										<Form.Item
											name="testing_number"
											rules={[{ required: true, message: 'Please enter your phone number' }]}
											className="!mb-2"
										>
											<PhoneNumberInput name="testing_number" />
											{sendError && (
												<div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
													<AlertCircle className="h-4 w-4" />
													<span>{sendError}</span>
												</div>
											)}
										</Form.Item>

										{form.getFieldValue('testing_number') && !verified && (
											<div className="mb-6">
												{!otpSent ? (
													<Button
														type="primary"
														onClick={handleSendOTP}
														loading={sending}
														className="h-12 bg-[#6366F1] hover:bg-[#5558E1] border-0 w-full"
													>
														Send Verification Code
													</Button>
												) : (
													<div className="space-y-3">
														<div>
															<Input
																placeholder="Enter verification code"
																value={otp}
																onChange={(e) =>
																	setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
																}
																className="h-12"
																maxLength={6}
																status={otpError ? 'error' : undefined}
																prefix={<LockIcon className="h-5 w-5 text-gray-400 mr-1" />}
																size="large"
															/>
															{otpError && (
																<div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
																	<AlertCircle className="h-4 w-4" />
																	<span>{otpError}</span>
																</div>
															)}
														</div>
														<div className="flex gap-3">
															<Button
																type="primary"
																onClick={handleVerifyOTP}
																loading={verifying}
																className="flex-1 h-12 bg-[#6366F1] hover:bg-[#5558E1] border-0"
															>
																Verify Code
															</Button>
															<Button
																onClick={handleSendOTP}
																loading={sending}
																className="flex-1 h-12 border-[#6366F1] text-[#6366F1] hover:text-[#5558E1] hover:border-[#5558E1]"
															>
																Resend Code
															</Button>
														</div>
													</div>
												)}
											</div>
										)}
									</Form>
								</motion.div>
							)}
						</div>
					) : (
						<div className="flex justify-center items-center h-full p-6">
							<Spin size="large" />
						</div>
					)}
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),
		'receive-call': (
			<div className="step-container">
				<div className="step-content">
					<div className="step-header">
						<h1 className="sm:text-3xl text-2xl font-bold mb-0">Receive a Call</h1>
						<div />
					</div>

					{!(isSubmitting || isLoading) ? (
						<div className="step-body">
							<AnimatePresence mode="wait">
								{hasTestingNumber && !showChangeNumber ? (
									<motion.div
										className="phone-setup-container"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -20 }}
										transition={{ duration: 0.3 }}
									>
										<motion.div
											className="setup-icon-container"
											animate={{
												scale: [1, 1.1, 1],
												rotate: [0, 5, -5, 0],
											}}
											transition={{
												duration: 2,
												repeat: Infinity,
												ease: 'easeInOut',
											}}
										>
											<PhoneIncoming className="setup-icon text-[#6366F1]" />
										</motion.div>

										<motion.div
											className="text-center space-y-4"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											transition={{ delay: 0.2 }}
										>
											<Title level={3}>
												You will receive a call at {selectedAgent?.testing_number}
											</Title>
											<div className="flex justify-center gap-4">
												<Button
													type="default"
													onClick={() => {
														form.resetFields();
														setShowChangeNumber(true);
														setVerified(false);
														setOtpSent(false);
														setOtp('');
														setSendError('');
														setOtpError('');
													}}
													className="h-12 px-6 flex items-center gap-2 border-[#6366F1] text-[#6366F1] hover:text-[#5558E1] hover:border-[#5558E1] hover:bg-indigo-50 transition-colors"
													icon={<Phone className="h-4 w-4" />}
												>
													Use Different Phone Number
												</Button>
											</div>
										</motion.div>
									</motion.div>
								) : (
									<motion.div
										className="phone-setup-container"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -20 }}
										transition={{ duration: 0.3 }}
									>
										<motion.div
											className="setup-icon-container"
											animate={{
												scale: [1, 1.1, 1],
											}}
											transition={{
												duration: 2,
												repeat: Infinity,
												ease: 'easeInOut',
											}}
										>
											<Phone className="setup-icon text-[#6366F1]" />
										</motion.div>

										<Title level={3}>
											Please enter your testing phone number, you'll receive a call on it from your
											AI Receptionist
										</Title>

										<Form
											form={form}
											layout="vertical"
											className="phone-form-tr"
											onValuesChange={(_, allValues) => {
												if (allValues.testing_number) {
													setCurrentPhoneNumber(allValues.testing_number);
												}
											}}
										>
											<Form.Item
												name="testing_number"
												rules={[
													{
														required: true,
														message: 'Please enter your phone number',
													},
												]}
											>
												<PhoneNumberInput name="testing_number" />
											</Form.Item>

											{form.getFieldValue('testing_number') && !verified && (
												<div className="mb-6">
													{!otpSent ? (
														<Button
															type="primary"
															onClick={handleSendOTP}
															loading={sending}
															className="h-12 bg-[#6366F1] hover:bg-[#5558E1] border-0 w-full"
														>
															Send Verification Code
														</Button>
													) : (
														<div className="space-y-3">
															<div>
																<Input
																	placeholder="Enter verification code"
																	value={otp}
																	onChange={(e) =>
																		setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
																	}
																	className="h-12"
																	maxLength={6}
																	status={otpError ? 'error' : undefined}
																	prefix={<LockIcon className="h-5 w-5 text-gray-400 mr-1" />}
																	size="large"
																/>
																{otpError && (
																	<div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
																		<AlertCircle className="h-4 w-4" />
																		<span>{otpError}</span>
																	</div>
																)}
															</div>
															<div className="flex gap-3">
																<Button
																	type="primary"
																	onClick={handleVerifyOTP}
																	loading={verifying}
																	className="flex-1 h-12 bg-[#6366F1] hover:bg-[#5558E1] border-0"
																>
																	Verify Code
																</Button>
																<Button
																	onClick={handleSendOTP}
																	loading={sending}
																	className="flex-1 h-12 border-[#6366F1] text-[#6366F1] hover:text-[#5558E1] hover:border-[#5558E1]"
																>
																	Resend Code
																</Button>
															</div>
														</div>
													)}
												</div>
											)}
										</Form>
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					) : (
						<div className="flex justify-center items-center h-full p-6">
							<Spin size="large" />
						</div>
					)}
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),

		'tweak-ai': (
			<div className="step-container" style={{ margin: 0, padding: 0 }}>
				<div className="step-content">
					<div className="step-header">
						<button
							onClick={() => setSelectedOption(null)}
							className="group flex items-center px-2 py-1 md:px-4 mdpy-2 text-blue-600 bg-white rounded-lg border border-blue-200 shadow-sm hover:bg-blue-50 transition-all duration-200 font-medium"
						>
							<ArrowLeft
								size={18}
								className="text-blue-600 transition-all duration-200 group-hover:scale-125"
							/>
							<span className="hidden md:block">Go Back</span>
						</button>

						<h1 className="sm:text-3xl text-2xl font-bold mb-0" />
						<div />
					</div>

					<div className="step-body" style={{ margin: 0, padding: 10 }}>
						<AICustomizationStep
							onTest={() => setSelectedOption('test-ai')}
							debrand={!!loggedInUser?.resellerClient}
						/>
					</div>
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),

		'start-trial': (
			<div className="step-container">
				<div className="step-content">
					<div className="step-header">
						<X
							onClick={() => setSelectedOption(null)}
							className="hover:cursor-pointer hover:bg-gray-200 rounded-lg"
						/>
						<h1 className="sm:text-3xl text-2xl font-bold mb-0">Start Your 7-Day Free Trial</h1>
						<div />
					</div>

					<div className="step-body">
						<div className="text-center space-y-6">
							<p className="text-lg text-gray-600 mb-6 text-center">
								{(selectedAgent?.type === 'Outbound' &&
									(loggedInUser.outboundSubscriptionStatus === 'canceled' ||
										loggedInUser?.outboundDemo)) ||
								(selectedAgent?.type === 'Inbound' &&
									(loggedInUser.inboundSubscriptionStatus === 'canceled' ||
										loggedInUser?.inboundDemo))
									? 'Start your 7 day free trial to get a standalone number that you can forward calls to.'
									: 'Your trial is already started!'}
							</p>

							{((selectedAgent?.type === 'Outbound' &&
								(loggedInUser.outboundSubscriptionStatus === 'canceled' ||
									loggedInUser.outboundSubscriptionStatus === undefined ||
									loggedInUser.outboundSubscriptionStatus === null)) ||
								(selectedAgent?.type === 'Inbound' &&
									(loggedInUser.inboundSubscriptionStatus === 'canceled' ||
										loggedInUser.inboundSubscriptionStatus === undefined ||
										loggedInUser.inboundSubscriptionStatus === null))) && (
								<PlansModal isComponentMode />
							)}

							{!(
								(loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
								(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)
							) && (
								<motion.button
									className="mark-complete-button mt-8"
									onClick={handleMarkAsComplete}
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									<CheckCircle className="h-5 w-5 mr-2" />
									Okay, take me back.
								</motion.button>
							)}
						</div>
					</div>
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),
		'get-number': (
			<div className="step-container">
				<div className="step-content">
					<div className="step-header">
						<X
							onClick={() => setSelectedOption(null)}
							className="hover:cursor-pointer hover:bg-gray-200 rounded-lg"
						/>
						<h1 className="sm:text-3xl text-2xl font-bold mb-0">Get Your AI Number</h1>
						<div />
					</div>

					<div className="step-body">
						<p className="text-lg text-gray-600 mb-6 text-center">
							Click the button below to pick your favorite AI number.
						</p>
						<PhoneNumberSettings
							renderCentralComponent
							debrand={!!loggedInUser?.resellerClient}
							setExtProvisionedNumber={(number: string) => {
								if (number) {
									setProvisionedNumber(number);
									setSelectedOption(null);
								}
							}}
						/>
					</div>
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),
		'forward-calls': (
			<div className="step-container">
				<div className="step-content">
					<div className="step-header">
						{!selectedAgent?.onboardingCompleted && (
							<X
								onClick={() => setSelectedOption(null)}
								className="hover:cursor-pointer hover:bg-gray-200 rounded-lg"
							/>
						)}
						<h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-0">
							Set Up Call Forwarding
						</h1>
					</div>

					<div className="step-body px-4 md:px-6 lg:px-8 py-6">
						<div className="text-center space-y-4 md:space-y-6">
							<Tooltip title="Your AI Phone Number.">
								{provisionedNumber ? (
									<h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-button-success mb-2">
										📞
										<CopyableText text={provisionedNumber} />
									</h1>
								) : (
									<Spin size="large" />
								)}
							</Tooltip>

							{/* Forwarding Setup Instruction Component */}
							<div className="w-full max-w-3xl mx-auto">
								<ForwardingSetupInstructions />
							</div>
							{!selectedAgent.onboardingCompleted && (
								<motion.button
									className="mark-complete-button mt-6 md:mt-8"
									onClick={() => forwardingCompleteMutation.mutate()}
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
									disabled={forwardingCompleteMutation.isPending}
								>
									<CheckCircle className="h-4 w-4 md:h-5 md:w-5 mr-2" />
									{forwardingCompleteMutation.isPending ? 'Saving...' : 'Mark as Completed'}
								</motion.button>
							)}
						</div>
					</div>
				</div>

				{!loggedInUser?.resellerClient && (
					<CsmCard
						avatarUrl={csmMember.imageUrl || ''}
						name={csmMember.name || 'N/A'}
						email={csmMember.email || 'N/A'}
						description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
						calendlyLink={csmMember.calendlyLink}
						className="!rounded-3xl !mx-0"
					/>
				)}
			</div>
		),
	};

	const renderContent = () => {
		if (selectedAgent.number && !selectedOption) {
			const isFirstTimeUser =
				callLogsLength === 0 &&
				(loggedInUser?.resellerClient
					? true
					: (loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
						(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)) &&
				!testCompleted;

			if (isFirstTimeUser && !isHireReceptionistPage) {
				return (
					<div className="test-receptionist-container">
						<div className="test-receptionist-content !relative">
							<div className="test-receptionist-left">
								<div className="icon-container">
									<img
										src={loggedInUser?.resellerClient ? resellerLogoUrl : '/new_logo.png'}
										alt=""
										className="w-12 h-12"
									/>
								</div>

								<Title className="!mb-4">Test Your AI Receptionist</Title>

								{renderFirstTimeOptions()}

								<div className="!mt-6 !text-center !max-w-lg !mx-auto">
									<p className="!text-gray-500 !text-sm !leading-relaxed !font-normal !mb-0">
										We recommend testing before getting your AI number
									</p>
								</div>
							</div>

							<Button
								onClick={handleMarkAsComplete}
								className="!absolute !bottom-6 !right-6 !bg-blue-600 !hover:bg-blue-700 !text-white !font-medium !px-8 !py-3"
							>
								Skip
							</Button>
						</div>

						{!loggedInUser?.resellerClient && (
							<CsmCard
								avatarUrl={csmMember.imageUrl || ''}
								name={csmMember.name || 'N/A'}
								email={csmMember.email || 'N/A'}
								description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
								calendlyLink={csmMember.calendlyLink}
								className="!rounded-3xl !mx-0"
							/>
						)}
					</div>
				);
			}
			const testedBefore = testCompleted || (selectedAgent.totalCalls || 0) > 0;

			if (selectedAgent.onboardingCompleted && !isHireReceptionistPage && provisionedNumber) {
				return content['forward-calls'];
			}

			return (
				<div className="test-receptionist-container">
					<div className="test-receptionist-content">
						<div className="test-receptionist-left">
							<div className="icon-container">
								<img
									src={loggedInUser?.resellerClient ? resellerLogoUrl : '/new_logo.png'}
									alt=""
									className="w-12 h-12"
								/>
							</div>

							<Title className="!mb-8">
								{isHireReceptionistPage ||
								(testedBefore &&
									((loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
										(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)))
									? 'Start your 24/7 Receptionist, free for 7 days.'
									: 'Your AI Receptionist is ready'}
							</Title>

							{getProvisionNumberMutation.status === 'pending' ? (
								<div className="flex justify-center items-center h-full p-6">
									<Spin size="large" />
								</div>
							) : (
								<div className="options-grid">
									{getProvisionNumberMutation.status === 'success' &&
										!(
											(loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
											(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)
										) &&
										!provisionedNumber && (
											<motion.div
												whileHover={{ scale: 1.02 }}
												whileTap={{ scale: 0.98 }}
												className="option-card relative highlight-card"
												animate={{
													scale: [1, 1.02, 1],
													boxShadow: [
														'0 0 0 0 rgba(99, 102, 241, 0)',
														'0 0 0 15px rgba(99, 102, 241, 0.1)',
														'0 0 0 0 rgba(99, 102, 241, 0)',
													],
												}}
												transition={{
													duration: 2,
													repeat: Infinity,
													ease: 'easeInOut',
												}}
												onHoverStart={() => setIsHovered('get-number')}
												onHoverEnd={() => setIsHovered(null)}
												onClick={() => handleOptionSelect('get-number')}
											>
												<div className="absolute top-3 right-3 bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded-full">
													Recommended
												</div>
												<div className="option-icon-container bg-blue-100">
													<Rocket className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
												</div>
												<h3>Get Your AI Number</h3>
												<p className="text-gray-600">Get an AI business phone number</p>
											</motion.div>
										)}

									{/* Only show these buttons if user is not in demo mode and not on hire page */}
									{getProvisionNumberMutation.status === 'success' &&
										!(
											(loggedInUser?.inboundDemo && selectedAgent?.type === 'Inbound') ||
											(selectedAgent?.type === 'Outbound' && loggedInUser.outboundDemo)
										) &&
										!isHireReceptionistPage && (
											<>
												<motion.div
													whileHover={{ scale: 1.02 }}
													whileTap={{ scale: 0.98 }}
													className="option-card"
													onHoverStart={() => setIsHovered('test-ai')}
													onHoverEnd={() => setIsHovered(null)}
													onClick={() => handleOptionSelect('test-ai')}
												>
													<div className="option-icon-container bg-blue-100">
														{isLoadingCredits && selectedOptionLoading === 'test-ai' ? (
															<Spin size="large" />
														) : (
															<Phone className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
														)}
													</div>
													<h3>Test Again</h3>
													<p className="text-gray-600">Try out your AI Receptionist again</p>
												</motion.div>

												<motion.div
													whileHover={{ scale: 1.02 }}
													whileTap={{ scale: 0.98 }}
													className="option-card"
													onHoverStart={() => setIsHovered('tweak-ai')}
													onHoverEnd={() => setIsHovered(null)}
													onClick={() => handleOptionSelect('tweak-ai')}
												>
													<div className="option-icon-container bg-blue-100">
														{isLoadingCredits && selectedOptionLoading === 'tweak-ai' ? (
															<Spin size="large" />
														) : (
															<Settings className="h-6 w-6 md:h-8 md:w-8 text-blue-600" />
														)}
													</div>
													<h3>Tweak My AI</h3>
													<p className="text-gray-600">Fine-tune your AI Receptionist</p>
												</motion.div>
											</>
										)}

									{renderTrialCard()}
								</div>
							)}
						</div>
					</div>
					{!loggedInUser?.resellerClient && (
						<CsmCard
							avatarUrl={csmMember.imageUrl || ''}
							name={csmMember.name || 'N/A'}
							email={csmMember.email || 'N/A'}
							description="As your Customer Success Manager, I'm here to help you get the most out of our platform. Feel free to book a call with me to discuss any questions or concerns you may have."
							calendlyLink={csmMember.calendlyLink}
							className="!rounded-3xl !mx-0"
						/>
					)}
				</div>
			);
		} else if (!selectedAgent.number || getProvisionNumberMutation.status === 'pending') {
			return <Spin size="large" />;
		}

		return (
			<AnimatePresence mode="wait">
				<motion.div
					key={selectedOption}
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -20 }}
					transition={{ type: 'spring', stiffness: 200, damping: 20 }}
					className="w-full"
				>
					{selectedOption ? content[selectedOption] : <Spin size="large" />}
				</motion.div>
			</AnimatePresence>
		);
	};

	if (showVerification) {
		return renderVerificationScreen();
	}

	return (
		<div className="test-receptionist-wrapper">
			{renderContent()}
			<SourceSelectorModal
				isOpen={isSourceSelectorModalOpen}
				onClose={() => setIsSourceSelectorModalOpen(false)}
				onComplete={handleSourceSelectorComplete}
			/>
			<InsufficientCreditsModal
				isOpen={insufficientCreditsModalVisible}
				onClose={() => setInsufficientCreditsModalVisible(false)}
				isDemo={
					((loggedInUser?.inboundDemo || loggedInUser?.inboundSubscriptionStatus === 'canceled') &&
						selectedAgent?.type === 'Inbound') ||
					(selectedAgent?.type === 'Outbound' &&
						(loggedInUser.outboundDemo || loggedInUser.outboundSubscriptionStatus === 'canceled'))
				}
				onStartTrial={handleStartTrial}
				onManageCredits={handleManageCredits}
			/>
		</div>
	);
};

export default TestReceptionist;
